import * as api from '@gitbook/api-client';
import { createPalette, type PaletteCommandDef, type PaletteGroupDef } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import type { OverlayTriggerState } from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

import {
    excludeSiteExternalLinks,
    getSiteSectionGroupChildren,
    type FeatureUpgradeAction,
    useTrialingSiteFeaturePlan,
} from '../../../state';
import { getPaidIndicatorBadgeProps } from '../PaidFeatureIndicator';
import { SiteSectionGroupIcon } from '../SiteSectionGroupIcon';
import { SiteSectionIcon } from '../SiteSectionIcon';
import { addSectionsToGroup } from './addSectionsToGroup';

export interface UseAddSectionsToGroupProps {
    organization: api.Organization;
    site: api.Site;
    siteStructure: api.SiteStructure;
    createSiteSectionDialogTrigger: OverlayTriggerState;
    upgrade: FeatureUpgradeAction | undefined;
    includeExistingSections?: boolean;
}

export function useAddSectionsToGroup(props: UseAddSectionsToGroupProps) {
    const {
        createSiteSectionDialogTrigger,
        siteStructure,
        organization,
        site,
        upgrade,
        includeExistingSections = true,
    } = props;
    const trialingPlan = useTrialingSiteFeaturePlan(site, api.SiteFeatureKey.SitesSections);
    return useEventCallback((siteSectionGroup: api.SiteSectionGroup) => {
        const newSectionGroup: PaletteGroupDef = {
            key: 'new-section',
            commands: [
                {
                    key: 'new-section',
                    title: 'New section',
                    icon: Icon.AddSiteSection,
                    trigger: upgrade?.onPress ?? (() => createSiteSectionDialogTrigger.open()),
                    badge: getPaidIndicatorBadgeProps({ upgrade, trialingPlan }),
                    upgrade: Boolean(upgrade),
                },
            ],
        };

        if (!includeExistingSections || siteStructure.type !== 'sections') {
            return createPalette({ groups: [newSectionGroup] });
        }

        const toSectionCommand = (siteSection: api.SiteSection): PaletteCommandDef => ({
            key: siteSection.id,
            title: siteSection.title,
            icon: <SiteSectionIcon siteSection={siteSection} />,
            trigger: () =>
                addSectionsToGroup({
                    sectionGroup: siteSectionGroup,
                    siteSections: [siteSection],
                    organization,
                    site,
                }),
        });

        // Mirror the section-group tree: direct sections are rows and each child group is a
        // submenu, recursing into nested subgroups. Sections already in the target group are
        // skipped, and a group whose whole subtree has none left is hidden.
        const toCommands = (
            items: (api.SiteSection | api.SiteSectionGroup | api.SiteExternalLink)[]
        ): PaletteCommandDef[] =>
            items.flatMap<PaletteCommandDef>((item) => {
                if (item.object === 'site-section') {
                    return item.sectionGroup === siteSectionGroup.id
                        ? []
                        : [toSectionCommand(item)];
                }
                if (item.object === 'site-external-link') {
                    return [];
                }
                const childCommands = toCommands(getSiteSectionGroupChildren(item));
                if (childCommands.length === 0) {
                    return [];
                }
                return [
                    {
                        key: `group-${item.id}`,
                        title: item.title,
                        icon: <SiteSectionGroupIcon siteSectionGroup={item} />,
                        descendant: () =>
                            createPalette({
                                search: { placeholder: 'Find a section…' },
                                groups: [{ key: 'sections', commands: childCommands }],
                            }),
                    },
                ];
            });

        return createPalette({
            search: { placeholder: 'Find a section…' },
            groups: [
                newSectionGroup,
                {
                    key: 'sections',
                    commands: toCommands(excludeSiteExternalLinks(siteStructure.structure)),
                },
            ],
        });
    });
}
